import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowLeftRight, ThumbsUp, ThumbsDown, CheckCircle, XCircle,
  Sparkles, Loader2, RefreshCw, ChevronDown,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Climate: "#10B981", Housing: "#3B82F6", Economy: "#F59E0B",
  Indigenous: "#DC2626", Disability: "#8B5CF6", Environment: "#059669",
  Integrity: "#6366F1", Defence: "#1E40AF", Education: "#0EA5E9",
  "Social Policy": "#EC4899", "Gambling Reform": "#D97706",
  "Foreign Policy": "#64748B", Energy: "#EF4444", Immigration: "#7C3AED",
  Agriculture: "#065F46", "Digital Safety": "#0891B2", "Human Rights": "#BE185D",
  default: "#6B7280",
};

function renderAnalysis(text, textColor) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    if (line.startsWith("**") && line.endsWith("**")) {
      return (
        <h3 key={i} className="text-base font-black mt-5 mb-1.5" style={{ color: "#FFC82E", fontFamily: "Outfit, sans-serif" }}>
          {line.replace(/\*\*/g, "")}
        </h3>
      );
    }
    if (!line.trim()) return <div key={i} className="h-2" />;
    return <p key={i} className="text-sm leading-relaxed mb-1" style={{ color: textColor }}>{line}</p>;
  });
}

function PoliticianSelector({ label, value, politicians, onChange, isDark }) {
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const surface = isDark ? "#1E293B" : "#FFFFFF";

  return (
    <div className="flex-1 min-w-0">
      <label className="block text-xs font-black mb-2 uppercase tracking-wider" style={{ color: textSec }}>
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none border-2 border-black px-3 py-2.5 pr-8 text-sm font-semibold outline-none"
          style={{ backgroundColor: surface, color: value ? text : textSec }}
          data-testid={`selector-${label.toLowerCase().replace(/\s/g, "-")}`}
        >
          <option value="">— Select a politician —</option>
          {politicians.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.party_name})
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: textSec }} />
      </div>
      {/* Selected card */}
      {value && (() => {
        const p = politicians.find((x) => x.id === value);
        if (!p) return null;
        return (
          <div className="mt-2 border-2 border-black p-3" style={{ backgroundColor: surface, borderLeftColor: p.party_color, borderLeftWidth: 4 }}>
            <div className="font-black text-sm" style={{ color: text }}>{p.name}</div>
            <div className="text-xs mt-0.5" style={{ color: textSec }}>{p.role} · {p.chamber === "House of Representatives" ? "House" : "Senate"} · {p.state}</div>
            <div className="mt-1.5">
              <span className="text-xs font-black px-2 py-0.5 border border-black text-white" style={{ backgroundColor: p.party_color }}>
                {p.party_name}
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default function PoliticianComparePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDark } = useTheme();

  const [politicians, setPoliticians] = useState([]);
  const [p1Id, setP1Id] = useState(searchParams.get("p1") || "");
  const [p2Id, setP2Id] = useState(searchParams.get("p2") || "");
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCached, setAiCached] = useState(false);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    axios.get(`${API}/politicians`).then((r) => setPoliticians(r.data)).catch(() => {});
  }, []);

  const runCompare = useCallback(async () => {
    if (!p1Id || !p2Id || p1Id === p2Id) return;
    setComparing(true);
    setComparison(null);
    setAiAnalysis("");
    try {
      const r = await axios.post(`${API}/politicians/compare`, {
        politician_1_id: p1Id,
        politician_2_id: p2Id,
      });
      setComparison(r.data);
    } catch {
      setComparison(null);
    } finally {
      setComparing(false);
    }
  }, [p1Id, p2Id]);

  useEffect(() => {
    if (p1Id && p2Id && p1Id !== p2Id) {
      setSearchParams({ p1: p1Id, p2: p2Id });
      runCompare();
    }
  }, [p1Id, p2Id, setSearchParams, runCompare]);

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/politicians/compare/ai-analysis`, {
        politician_1_id: p1Id,
        politician_2_id: p2Id,
      });
      setAiAnalysis(r.data.analysis);
      setAiCached(r.data.cached);
    } catch {
      setAiAnalysis("Unable to generate analysis. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const swapPoliticians = () => {
    const tmp = p1Id;
    setP1Id(p2Id);
    setP2Id(tmp);
  };

  const canCompare = p1Id && p2Id && p1Id !== p2Id;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="compare-politicians-page">
      {/* Hero */}
      <div className="py-10 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate("/politicians")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
            data-testid="back-politicians-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All Politicians
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="px-3 py-1 text-xs font-black border-2 border-black" style={{ backgroundColor: "#FFC82E", color: "#000" }}>
              COMPARE
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Compare Politicians
          </h1>
          <p className="text-sm" style={{ color: "#94A3B8" }}>
            See how two politicians voted on the same bills — and where they agree or diverge.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Selectors */}
        <section>
          <div className="flex items-end gap-3 flex-wrap">
            <PoliticianSelector
              label="Politician 1"
              value={p1Id}
              politicians={politicians}
              onChange={setP1Id}
              isDark={isDark}
            />
            {/* Swap */}
            <button
              onClick={swapPoliticians}
              disabled={!p1Id && !p2Id}
              className="flex-shrink-0 mb-0.5 p-2.5 border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-40"
              style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0 #000" }}
              data-testid="swap-politicians-btn"
              title="Swap politicians"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <PoliticianSelector
              label="Politician 2"
              value={p2Id}
              politicians={politicians}
              onChange={setP2Id}
              isDark={isDark}
            />
          </div>
          {p1Id === p2Id && p1Id && (
            <p className="text-xs mt-2 font-bold" style={{ color: "#EF4444" }}>
              Please select two different politicians.
            </p>
          )}
        </section>

        {/* Loading */}
        {comparing && (
          <div className="flex items-center justify-center py-16 border-2 border-black" style={{ backgroundColor: surface }}>
            <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: "#FFC82E" }} />
            <span className="font-bold text-sm" style={{ color: text }}>Comparing voting records…</span>
          </div>
        )}

        {/* Results */}
        {comparison && !comparing && (
          <>
            {/* Stats */}
            <section data-testid="comparison-stats">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Shared Votes", value: comparison.stats.total_shared, color: "#FFC82E" },
                  { label: "Agreed", value: comparison.stats.agreement_count, color: "#10B981" },
                  { label: "Disagreed", value: comparison.stats.disagreement_count, color: "#EF4444" },
                  { label: "Agreement Rate", value: `${comparison.stats.agreement_pct}%`, color: "#38BDF8" },
                ].map(({ label, value, color }) => (
                  <div
                    key={label}
                    className="border-2 border-black p-4 text-center"
                    style={{ backgroundColor: surface, boxShadow: "2px 2px 0 #000" }}
                    data-testid={`compare-stat-${label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    <div className="text-2xl font-black" style={{ color }}>{value}</div>
                    <div className="text-xs font-bold mt-0.5" style={{ color: textSec }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Visual agreement bar */}
              {comparison.stats.total_shared > 0 && (
                <div className="mt-3 border-2 border-black overflow-hidden h-4 flex" style={{ boxShadow: "2px 2px 0 #000" }}>
                  <div
                    className="h-full transition-all"
                    style={{ width: `${comparison.stats.agreement_pct}%`, backgroundColor: "#10B981" }}
                  />
                  <div
                    className="h-full flex-1"
                    style={{ backgroundColor: "#EF4444" }}
                  />
                </div>
              )}
            </section>

            {/* Shared votes table */}
            {comparison.shared_votes.length > 0 && (
              <section data-testid="shared-votes-section">
                <h2
                  className="text-xl font-black mb-4 pb-2 border-b-2 border-black"
                  style={{ color: text, fontFamily: "Outfit, sans-serif" }}
                >
                  Votes on the Same Bills
                  <span className="text-sm font-bold ml-2" style={{ color: textSec }}>
                    ({comparison.shared_votes.length} shared)
                  </span>
                </h2>
                <div className="space-y-2">
                  {comparison.shared_votes.map((sv) => {
                    const catColor = CATEGORY_COLORS[sv.category] || CATEGORY_COLORS.default;
                    const p1 = comparison.politician_1;
                    const p2 = comparison.politician_2;
                    const svKey = `${sv.bill}-${sv.date}`;
                    return (
                      <div
                        key={svKey}
                        className="border-2 border-black p-4"
                        style={{
                          backgroundColor: sv.agreed ? (isDark ? "#0D2418" : "#F0FDF4") : (isDark ? "#1F0A0A" : "#FEF2F2"),
                          boxShadow: "2px 2px 0 #000",
                        }}
                        data-testid={`shared-vote-${sv.bill.slice(0, 20).replace(/\s+/g, "-")}`}
                      >
                        {/* Bill name + meta */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-black text-sm" style={{ color: text }}>{sv.bill}</span>
                          <span className="text-xs" style={{ color: textSec }}>{sv.date}</span>
                          <span className="text-xs font-bold px-1.5 py-0.5 border" style={{ color: catColor, borderColor: catColor }}>
                            {sv.category}
                          </span>
                          <div className="flex items-center gap-1 text-xs font-bold" style={{ color: sv.passed ? "#10B981" : "#EF4444" }}>
                            {sv.passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {sv.passed ? "Passed" : "Not passed"}
                          </div>
                          <div
                            className="ml-auto text-xs font-black px-2 py-0.5 border-2 border-black"
                            style={{ backgroundColor: sv.agreed ? "#10B981" : "#EF4444", color: "#fff" }}
                          >
                            {sv.agreed ? "AGREED" : "DISAGREED"}
                          </div>
                        </div>
                        {/* Vote badges */}
                        <div className="flex gap-4 flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2" style={{ backgroundColor: p1.party_color }} />
                            <span className="text-xs font-bold" style={{ color: textSec }}>{p1.name.split(" ").pop()}:</span>
                            {sv.p1_vote === "aye" ? (
                              <div className="flex items-center gap-1 text-xs font-black px-2 py-0.5 border border-black text-white" style={{ backgroundColor: "#10B981" }}>
                                <ThumbsUp className="w-3 h-3" /> AYE
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-xs font-black px-2 py-0.5 border border-black text-white" style={{ backgroundColor: "#EF4444" }}>
                                <ThumbsDown className="w-3 h-3" /> NO
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2" style={{ backgroundColor: p2.party_color }} />
                            <span className="text-xs font-bold" style={{ color: textSec }}>{p2.name.split(" ").pop()}:</span>
                            {sv.p2_vote === "aye" ? (
                              <div className="flex items-center gap-1 text-xs font-black px-2 py-0.5 border border-black text-white" style={{ backgroundColor: "#10B981" }}>
                                <ThumbsUp className="w-3 h-3" /> AYE
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-xs font-black px-2 py-0.5 border border-black text-white" style={{ backgroundColor: "#EF4444" }}>
                                <ThumbsDown className="w-3 h-3" /> NO
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {comparison.shared_votes.length === 0 && (
              <div className="border-2 border-black p-8 text-center" style={{ backgroundColor: surface }}>
                <p className="font-black text-base mb-1" style={{ color: text }}>No shared votes found</p>
                <p className="text-sm" style={{ color: textSec }}>
                  These two politicians don't share any recorded votes. Try a different pair.
                </p>
              </div>
            )}

            {/* AI Analysis */}
            <section data-testid="compare-ai-section">
              <h2
                className="text-xl font-black mb-4 pb-2 border-b-2 border-black flex items-center gap-2"
                style={{ color: text, fontFamily: "Outfit, sans-serif" }}
              >
                <Sparkles className="w-5 h-5" style={{ color: "#FFC82E" }} />
                AI Comparison Analysis
              </h2>

              {!aiAnalysis && (
                <div className="border-2 border-black p-6 text-center" style={{ backgroundColor: surface }}>
                  <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: "#FFC82E" }} />
                  <p className="font-black text-base mb-1" style={{ color: text }}>
                    Get a deep AI comparison
                  </p>
                  <p className="text-sm mb-4" style={{ color: textSec }}>
                    AI will analyse their political profiles, voting patterns, key differences, and who each politician represents.
                  </p>
                  <button
                    onClick={generateAI}
                    disabled={aiLoading}
                    className="flex items-center gap-2 mx-auto px-6 py-2.5 border-2 border-black font-black text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "3px 3px 0 #000" }}
                    data-testid="generate-compare-analysis-btn"
                  >
                    {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : <><Sparkles className="w-4 h-4" /> Generate Comparison</>}
                  </button>
                </div>
              )}

              {aiAnalysis && (
                <div className="border-2 border-black p-6" style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }} data-testid="compare-ai-content">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" style={{ color: "#FFC82E" }} />
                      <span className="text-xs font-black" style={{ color: "#FFC82E" }}>AI ANALYSIS — GPT-4o</span>
                      {aiCached && <span className="text-xs px-1.5 py-0.5 border font-bold" style={{ color: "#64748B", borderColor: "#64748B" }}>cached</span>}
                    </div>
                    <button
                      onClick={generateAI}
                      disabled={aiLoading}
                      className="flex items-center gap-1 text-xs font-bold hover:opacity-75 transition-opacity"
                      style={{ color: textSec }}
                      data-testid="regenerate-compare-btn"
                    >
                      {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Refresh
                    </button>
                  </div>
                  <div>{renderAnalysis(aiAnalysis, text)}</div>
                </div>
              )}
            </section>
          </>
        )}

        {/* Empty state */}
        {!canCompare && !comparing && (
          <div className="border-2 border-black p-10 text-center" style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}>
            <ArrowLeftRight className="w-10 h-10 mx-auto mb-3" style={{ color: "#FFC82E" }} />
            <p className="font-black text-lg mb-1" style={{ color: text }}>Select two politicians to compare</p>
            <p className="text-sm" style={{ color: textSec }}>
              Choose politicians from the dropdowns above to see how they voted on shared bills and get an AI comparison.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
